import { useCallback, useMemo } from 'react';
import { useSetAtom } from 'jotai';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useIntercom } from 'react-use-intercom';

import { EmployerUserContact } from '@restworld/utility-types';
import { Button as ButtonV2 } from '@restworld/ui-ds-base';
import { COLLABORATOR_ROLE_KEYS, useAuth, useIsEmployerAnAdmin } from '@restworld/feature-authentication';
import { SkeletonEmployerCardListLoading } from '@restworld/ui-components';
import { EmployerCollaboratorCard } from '../../legacy';

// import CollaboratorsPageHeader from '../collaborators-page-header';
import { EmployerPageContentWrapper } from '../../employer-page-content-wrapper';
import useEmployerUserContactsListQuery from '../hooks/use-employer-user-contacts-list-query';
import {
  collaboratorActionAtom,
  collaboratorActionModalVariantAtom,
  selectedCollaboratorAtom,
  useCollaboratorActionModal,
} from '../collaborators.atoms';
import CollaboratorActionModal from '../modals/collaborator-action-modal';
import { EmployerPrimaryLayoutHeader } from '../../../layouts';
import { cn } from '@restworld/utils-common';

function CollaboratorsPageContent() {
  const { t } = useTranslation('collaborators-page');
  const { employerUserContacts, hasNextPage, query } = useEmployerUserContactsListQuery();
  const { startTour } = useIntercom();
  const isAdmin = useIsEmployerAnAdmin();
  const { openCollaboratorActionModal } = useCollaboratorActionModal();
  const setCollaboratorAction = useSetAtom(collaboratorActionAtom);
  const setModalVariant = useSetAtom(collaboratorActionModalVariantAtom);

  const addCollaborator = useCallback(() => {
    setCollaboratorAction('create');
    setModalVariant('default');
    openCollaboratorActionModal();
  }, [openCollaboratorActionModal, setCollaboratorAction, setModalVariant]);

  return (
    <EmployerPageContentWrapper>
      {/* <CollaboratorsPageHeader /> */}
      <EmployerPrimaryLayoutHeader
        onAddButtonClick={addCollaborator}
        addButtonTitle={isAdmin ? t('add_collaborator') : null}
        onInfoIconClick={() => startTour(599328)}
        pageTitle={t('collaborators')}
      />
      <BodyWrapper>
        {query.isLoading ? (
          <SkeletonEmployerCardListLoading />
        ) : (
          <EmployerUserContactList contactList={employerUserContacts} />
        )}
        {hasNextPage && (
          <div className="flex justify-center pt-16">
            <ButtonV2
              StartIcon={PlusIcon}
              variant="tertiary"
              onClick={() => query.fetchNextPage()}
              loading={query.isFetchingNextPage}
            >
              {t('load_more_items')}
            </ButtonV2>
          </div>
        )}
      </BodyWrapper>
      <CollaboratorActionModal />
    </EmployerPageContentWrapper>
  );
}
export default CollaboratorsPageContent;

export function EmployerUserContactList({ contactList }: { contactList?: EmployerUserContact[] }) {
  return (
    <EmployerUserContactListWrapper>
      {contactList?.map((contact) => (
        <EmployerUserContactItem key={contact.user_id} contact={contact} />
      ))}
    </EmployerUserContactListWrapper>
  );
}

export function EmployerUserContactItem({ contact }: { contact: EmployerUserContact }) {
  const collaboratorItem = useEmployerUserContactItem(contact);
  const setSelectedCollaborator = useSetAtom(selectedCollaboratorAtom);
  const setCollaboratorAction = useSetAtom(collaboratorActionAtom);
  const setModalVariant = useSetAtom(collaboratorActionModalVariantAtom);
  const { openCollaboratorActionModal } = useCollaboratorActionModal();

  const onEditCollaborator = useCallback(() => {
    setSelectedCollaborator(contact);
    setModalVariant('default');
    setCollaboratorAction('update');
    openCollaboratorActionModal();
  }, [contact, openCollaboratorActionModal, setCollaboratorAction, setModalVariant, setSelectedCollaborator]);

  const onRemoveCollaborator = useCallback(() => {
    setSelectedCollaborator(contact);
    setCollaboratorAction('delete');
    setModalVariant('noHeading');
    openCollaboratorActionModal();
  }, [contact, openCollaboratorActionModal, setCollaboratorAction, setModalVariant, setSelectedCollaborator]);

  return (
    <EmployerCollaboratorCard
      onEditCollaborator={onEditCollaborator}
      onRemoveCollaborator={onRemoveCollaborator}
      {...collaboratorItem}
    />
  );
}

export function useEmployerUserContactItem(contact: EmployerUserContact) {
  const { user } = useAuth();
  const isAdmin = useIsEmployerAnAdmin();
  const {
    user: { email, phone_number, name, surname, email_verified },
    role_rw_key,
    updated_at,
    invited_at,
    invited_by,
  } = contact;
  const collaboratorItem = useMemo(
    () => ({
      email,
      firstName: name,
      lastName: surname,
      phoneNumber: phone_number,
      lastEdit: updated_at,
      isAdmin: role_rw_key === COLLABORATOR_ROLE_KEYS.ADMIN,
      isInvited: !email_verified && (!!invited_at || !!invited_by),
      areActionsDisabled: user?.id === contact.user_id || !isAdmin,
    }),
    [
      contact.user_id,
      email,
      email_verified,
      invited_at,
      invited_by,
      isAdmin,
      name,
      phone_number,
      role_rw_key,
      surname,
      updated_at,
      user?.id,
    ]
  );

  return collaboratorItem;
}

const EmployerUserContactListWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-3', className)} {...props} />
);
const BodyWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('pt-10 lg:pt-20', className)} {...props} />
);
